<template>
  <div class="performance-stage-form">
    <div class="progress-header">
      <div class="flex items-center justify-end">
        <span class="text-xs text-gray-500">Progress</span>
        <div class="w-32 bg-gray-200 rounded-full h-2 mx-2">
          <div
            class="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-500"
            :style="`width: ${progressPercentage}%`" />
        </div>
        <span class="text-xs font-medium text-gray-700">{{ progressPercentage }}%</span>
      </div>
    </div>

    <div
      v-if="loading"
      class="content-body">
      <div class="flex items-center justify-center py-8">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-3xl text-orange-600 mb-2" />
          <p class="text-sm text-gray-500">
            Loading performance stage...
          </p>
        </div>
      </div>
    </div>

    <div
      v-else
      class="content-body">
      <!-- View Mode -->
      <div v-if="!isEditing">
        <div class="space-y-4">
          <!-- Section 1: Performance Manager & First Call -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-user-tie text-gray-500 mr-2" />
              Performance Management
            </h6>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-user-shield mr-1.5" />
                  Customer Performance Manager
                </div>
                <div class="info-value">
                  {{ performanceStage.performanceManager?.displayName || 'Not assigned' }}
                </div>
              </div>

              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-calendar-check mr-1.5" />
                  First Performance Call Date
                </div>
                <div v-if="performanceStage.firstPerformanceCallDate">
                  <div class="text-sm text-gray-700">
                    {{ formatDateTime(performanceStage.firstPerformanceCallDate) }}
                  </div>
                </div>
                <div
                  v-else
                  class="text-sm text-gray-400">
                  Not scheduled
                </div>
              </div>
            </div>

            <div
              v-if="performanceStage.firstPerformanceCallNotes"
              class="mt-3">
              <div class="info-label mb-2">
                <i class="fas fa-sticky-note mr-1.5" />
                First Call Notes
              </div>
              <div class="notes-box">
                {{ performanceStage.firstPerformanceCallNotes }}
              </div>
            </div>
          </div>

          <!-- Section 2: Check-in Schedule -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-calendar-alt text-gray-500 mr-2" />
              Check-in Schedule
            </h6>
            <div class="info-item">
              <div class="info-label">
                <i class="fas fa-clock mr-1.5" />
                Frequency
              </div>
              <div v-if="performanceStage.checkInSchedule">
                <span class="text-sm font-medium text-orange-600">
                  {{ performanceStage.checkInScheduleDisplay }}
                </span>
              </div>
              <div
                v-else
                class="text-sm text-gray-400">
                Not set
              </div>
            </div>
          </div>

          <!-- Section 3: Dashboard Links -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-chart-bar text-gray-500 mr-2" />
              Reporting Dashboard Links
            </h6>
            <div class="space-y-3">
              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-eye mr-1.5" />
                  GPP Visibility Dashboard
                </div>
                <div v-if="performanceStage.gppVisibilityDashboardUrl">
                  <a
                    :href="performanceStage.gppVisibilityDashboardUrl"
                    target="_blank"
                    class="info-link">
                    <i class="fas fa-external-link-alt mr-1.5" />
                    Open Dashboard
                  </a>
                </div>
                <div
                  v-else
                  class="text-sm text-gray-400">
                  Not configured
                </div>
              </div>

              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-chart-line mr-1.5" />
                  Organic Movement Dashboard
                </div>
                <div v-if="performanceStage.organicMovementDashboardUrl">
                  <a
                    :href="performanceStage.organicMovementDashboardUrl"
                    target="_blank"
                    class="info-link">
                    <i class="fas fa-external-link-alt mr-1.5" />
                    Open Dashboard
                  </a>
                </div>
                <div
                  v-else
                  class="text-sm text-gray-400">
                  Not configured
                </div>
              </div>

              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-star mr-1.5" />
                  Reviews Dashboard
                </div>
                <div v-if="performanceStage.reviewsDashboardUrl">
                  <a
                    :href="performanceStage.reviewsDashboardUrl"
                    target="_blank"
                    class="info-link">
                    <i class="fas fa-external-link-alt mr-1.5" />
                    Open Dashboard
                  </a>
                </div>
                <div
                  v-else
                  class="text-sm text-gray-400">
                  Not configured
                </div>
              </div>
            </div>
          </div>

          <!-- Section 4: Next QBR -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-briefcase text-gray-500 mr-2" />
              Quarterly Business Review
            </h6>
            <div class="info-item">
              <div class="info-label">
                <i class="fas fa-calendar-alt mr-1.5" />
                Next QBR Date
              </div>
              <div v-if="performanceStage.nextQbrDate">
                <div class="text-sm text-gray-700">
                  {{ formatDate(performanceStage.nextQbrDate) }}
                </div>
                <div
                  v-if="isQbrUpcoming"
                  class="text-xs text-orange-600 mt-1">
                  <i class="fas fa-clock mr-1" />
                  {{ daysUntilQbr }} days away
                </div>
              </div>
              <div
                v-else
                class="text-sm text-gray-400">
                Not scheduled
              </div>
            </div>
          </div>

          <!-- Section 5: Monthly Review Calls -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-phone-alt text-gray-500 mr-2" />
              Monthly Review Calls
            </h6>
            <div v-if="reviewCalls.length > 0">
              <div class="space-y-2">
                <div
                  v-for="call in reviewCalls.slice(0, showAllCalls ? reviewCalls.length : 3)"
                  :key="call.id"
                  class="review-call-item">
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <div class="text-sm font-medium text-gray-900">
                        {{ call.shortFormattedDate }}
                      </div>
                      <div
                        v-if="call.notes"
                        class="text-sm text-gray-600 mt-1">
                        {{ call.notes }}
                      </div>
                    </div>
                    <div
                      v-if="!isEditing"
                      class="flex items-center space-x-2 ml-4">
                      <button
                        class="text-gray-400 hover:text-gray-600"
                        @click="editReviewCall(call)">
                        <i class="fas fa-edit" />
                      </button>
                      <button
                        class="text-gray-400 hover:text-red-600"
                        @click="deleteReviewCall(call.id)">
                        <i class="fas fa-trash" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="reviewCalls.length > 3 && !showAllCalls"
                class="mt-2">
                <button
                  class="text-sm text-orange-600 hover:text-orange-700"
                  @click="showAllCalls = true">
                  Show {{ reviewCalls.length - 3 }} more calls
                </button>
              </div>
            </div>
            <div
              v-else
              class="text-sm text-gray-400">
              No review calls recorded
            </div>
            <div class="mt-3">
              <button
                class="action-button action-button-secondary text-sm"
                @click="showAddCallForm = true">
                <i class="fas fa-plus mr-1.5" />
                Add Review Call
              </button>
            </div>

            <!-- Add/Edit Review Call Form -->
            <div
              v-if="showAddCallForm || editingCallId"
              class="mt-4 p-4 bg-gray-50 rounded-lg">
              <h6 class="text-sm font-medium text-gray-700 mb-3">
                {{ editingCallId ? 'Edit' : 'Add' }} Review Call
              </h6>
              <div class="space-y-3">
                <div class="form-group">
                  <label class="form-label">Call Date</label>
                  <input
                    v-model="callForm.call_date"
                    type="datetime-local"
                    class="form-input">
                </div>
                <div class="form-group">
                  <label class="form-label">Notes</label>
                  <textarea
                    v-model="callForm.notes"
                    rows="3"
                    class="form-input"
                    placeholder="Enter call notes..." />
                </div>
                <div class="flex gap-2">
                  <button
                    class="action-button action-button-success text-sm"
                    :disabled="savingCall"
                    @click="saveReviewCall">
                    <i
                      v-if="savingCall"
                      class="fas fa-spinner fa-spin mr-1.5" />
                    <i
                      v-else
                      class="fas fa-save mr-1.5" />
                    Save
                  </button>
                  <button
                    class="action-button action-button-secondary text-sm"
                    @click="cancelCallForm">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6">
          <button
            class="action-button action-button-primary"
            @click="startEditing">
            <i class="fas fa-edit mr-1.5" />
            Edit Performance Stage
          </button>
        </div>
      </div>

      <!-- Edit Mode -->
      <form
        v-else
        class="space-y-6"
        @submit.prevent="savePerformanceStage">
        <!-- Section 1: Performance Manager & First Call -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-user-tie text-gray-500 mr-2" />
            Performance Management
          </h6>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Customer Performance Manager</label>
              <select
                v-model="form.performance_manager_id"
                class="form-input">
                <option value="">
                  Select manager
                </option>
                <option
                  v-for="manager in performanceManagers"
                  :key="manager.id"
                  :value="manager.id">
                  {{ manager.displayName }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">First Performance Call Date</label>
              <input
                v-model="form.first_performance_call_date"
                type="datetime-local"
                class="form-input">
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">First Call Notes</label>
            <textarea
              v-model="form.first_performance_call_notes"
              rows="4"
              class="form-input"
              placeholder="Enter notes from the first performance call..." />
          </div>
        </div>

        <!-- Section 2: Check-in Schedule -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-calendar-alt text-gray-500 mr-2" />
            Check-in Schedule
          </h6>
          <div class="form-group">
            <label class="form-label">Check-in Frequency</label>
            <select
              v-model="form.check_in_schedule"
              class="form-input">
              <option value="">
                Select frequency
              </option>
              <option
                v-for="(label, value) in checkInSchedules"
                :key="value"
                :value="value">
                {{ label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Section 3: Dashboard Links -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-chart-bar text-gray-500 mr-2" />
            Reporting Dashboard Links
          </h6>
          <div class="space-y-4">
            <div class="form-group">
              <label class="form-label">GPP Visibility Dashboard URL</label>
              <input
                v-model="form.gpp_visibility_dashboard_url"
                type="url"
                class="form-input"
                placeholder="https://example.com/gpp-dashboard">
            </div>
            <div class="form-group">
              <label class="form-label">Organic Movement Dashboard URL</label>
              <input
                v-model="form.organic_movement_dashboard_url"
                type="url"
                class="form-input"
                placeholder="https://example.com/organic-dashboard">
            </div>
            <div class="form-group">
              <label class="form-label">Reviews Dashboard URL</label>
              <input
                v-model="form.reviews_dashboard_url"
                type="url"
                class="form-input"
                placeholder="https://example.com/reviews-dashboard">
            </div>
          </div>
        </div>

        <!-- Section 4: Next QBR -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-briefcase text-gray-500 mr-2" />
            Quarterly Business Review
          </h6>
          <div class="form-group">
            <label class="form-label">Next QBR Date</label>
            <input
              v-model="form.next_qbr_date"
              type="datetime-local"
              class="form-input">
          </div>
        </div>

        <div class="form-actions-sticky">
          <div class="form-actions">
            <div class="flex gap-2">
              <button
                type="submit"
                :disabled="saving"
                class="action-button action-button-success">
                <i
                  v-if="saving"
                  class="fas fa-spinner fa-spin mr-1.5" />
                <i
                  v-else
                  class="fas fa-save mr-1.5" />
                Save
              </button>
              <button
                type="button"
                :disabled="saving"
                class="action-button action-button-secondary"
                @click="cancelEditing">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const loading = ref(true)
const saving = ref(false)
const savingCall = ref(false)
const isEditing = ref(false)
const performanceStage = ref({})
const performanceManagers = ref([])
const reviewCalls = ref([])
const showAllCalls = ref(false)
const showAddCallForm = ref(false)
const editingCallId = ref(null)
const form = ref({
  performance_manager_id: '',
  first_performance_call_date: '',
  first_performance_call_notes: '',
  check_in_schedule: '',
  gpp_visibility_dashboard_url: '',
  organic_movement_dashboard_url: '',
  reviews_dashboard_url: '',
  next_qbr_date: ''
})
const callForm = ref({
  call_date: '',
  notes: ''
})

// Constants
const checkInSchedules = {
  daily: 'Daily',
  three_weekly: '3x Weekly',
  weekly: 'Weekly',
  bi_weekly: 'Bi-weekly',
  monthly: 'Monthly'
}

// Computed
const progressPercentage = computed(() => performanceStage.value?.progressPercentage || 0)

const isQbrUpcoming = computed(() => {
  if (!performanceStage.value?.nextQbrDate) return false
  const qbrDate = new Date(performanceStage.value.nextQbrDate)
  const now = new Date()
  return qbrDate > now
})

const daysUntilQbr = computed(() => {
  if (!performanceStage.value?.nextQbrDate) return 0
  const qbrDate = new Date(performanceStage.value.nextQbrDate)
  const now = new Date()
  const diffTime = qbrDate - now
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return Math.max(0, diffDays)
})

// Methods
const fetchReviewCalls = async() => {
  try {
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/performance_stage/performance_review_calls`)
    if (response.data.success) {
      reviewCalls.value = response.data.reviewCalls || []
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching review calls:', error)
  }
}

const fetchPerformanceStage = async() => {
  try {
    loading.value = true
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/performance_stage`)
    if (response.data.success) {
      performanceStage.value = response.data.performanceStage || {}
      performanceManagers.value = response.data.performanceManagers || []
      await fetchReviewCalls()
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching performance stage:', error)
    mdtoast.error('Failed to load performance stage', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const startEditing = () => {
  const stage = performanceStage.value
  form.value = {
    performance_manager_id: stage.performanceManagerId || '',
    first_performance_call_date: stage.firstPerformanceCallDate ? stage.firstPerformanceCallDate.slice(0, 16) : '',
    first_performance_call_notes: stage.firstPerformanceCallNotes || '',
    check_in_schedule: stage.checkInSchedule || '',
    gpp_visibility_dashboard_url: stage.gppVisibilityDashboardUrl || '',
    organic_movement_dashboard_url: stage.organicMovementDashboardUrl || '',
    reviews_dashboard_url: stage.reviewsDashboardUrl || '',
    next_qbr_date: stage.nextQbrDate ? stage.nextQbrDate.slice(0, 16) : ''
  }
  isEditing.value = true
}

const cancelEditing = () => {
  isEditing.value = false
}

const savePerformanceStage = async() => {
  try {
    saving.value = true
    const payload = { performance_stage: form.value }
    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/performance_stage`,
      payload
    )

    if (response.data.success) {
      performanceStage.value = response.data.performanceStage
      isEditing.value = false
      mdtoast.success('Performance stage saved successfully', { duration: 3000 })
    } else {
      mdtoast.error(response.data.error || 'Failed to save performance stage', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving performance stage:', error)
    mdtoast.error('Failed to save performance stage', { duration: 3000 })
  } finally {
    saving.value = false
  }
}

const editReviewCall = (call) => {
  editingCallId.value = call.id
  callForm.value = {
    call_date: call.callDate ? call.callDate.slice(0, 16) : '',
    notes: call.notes || ''
  }
  showAddCallForm.value = false
}

const cancelCallForm = () => {
  showAddCallForm.value = false
  editingCallId.value = null
  callForm.value = {
    call_date: '',
    notes: ''
  }
}

const saveReviewCall = async() => {
  try {
    savingCall.value = true
    const isEdit = !!editingCallId.value
    const url = isEdit ?
      `/ajax/organizations/${props.organizationId}/performance_stage/performance_review_calls/${editingCallId.value}` :
      `/ajax/organizations/${props.organizationId}/performance_stage/performance_review_calls`
    const method = isEdit ? 'patch' : 'post'

    const response = await axios[method](url, {
      performance_review_call: callForm.value
    })

    if (response.data.success) {
      await fetchReviewCalls()
      cancelCallForm()
      mdtoast.success(`Review call ${isEdit ? 'updated' : 'added'} successfully`, { duration: 3000 })
    } else {
      mdtoast.error(response.data.error || `Failed to ${isEdit ? 'update' : 'add'} review call`, { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving review call:', error)
    mdtoast.error('Failed to save review call', { duration: 3000 })
  } finally {
    savingCall.value = false
  }
}

const deleteReviewCall = async(callId) => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm('Are you sure you want to delete this review call?')) {
    return
  }

  try {
    const response = await axios.delete(`/ajax/organizations/${props.organizationId}/performance_stage/performance_review_calls/${callId}`)

    if (response.data.success) {
      await fetchReviewCalls()
      mdtoast.success('Review call deleted successfully', { duration: 3000 })
    } else {
      mdtoast.error('Failed to delete review call', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error deleting review call:', error)
    mdtoast.error('Failed to delete review call', { duration: 3000 })
  }
}

// Lifecycle
onMounted(() => {
  fetchPerformanceStage()
})
</script>

<style scoped>
.performance-stage-form {
  display: flex;
  flex-direction: column;
}

.progress-header {
  @apply mb-4;
}

.content-body {
  overflow-y: auto;
  flex: 1;
}

.section {
  @apply bg-gray-50 rounded-lg p-4 space-y-3;
}

.section-title {
  @apply text-sm font-semibold text-gray-700 mb-3 flex items-center;
}

.info-item {
  @apply space-y-1;
}

.info-label {
  @apply text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center;
}

.info-value {
  @apply text-sm text-gray-900;
}

.info-link {
  @apply text-sm text-orange-600 hover:text-orange-800 transition-colors
         inline-flex items-center hover:underline;
}

.notes-box {
  @apply bg-white rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap border border-gray-200;
}

.review-call-item {
  @apply bg-white rounded-md p-3 border border-gray-200;
}

.form-section {
  @apply bg-gray-50 rounded-lg p-4 space-y-4;
}

.form-section-title {
  @apply text-sm font-semibold text-gray-700 mb-3 flex items-center;
}

.form-group {
  @apply space-y-1;
}

.form-label {
  @apply block text-sm font-medium text-gray-700;
}

.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
         focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
         transition-colors duration-200;
}

.form-actions-sticky {
  position: sticky;
  bottom: 0;
  background-color: white;
  margin-top: 1.5rem;
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  margin-bottom: -1.25rem;
  padding: 0 1.25rem;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.05);
}

.form-actions {
  @apply flex items-center justify-between py-4 border-t border-gray-200;
}

.action-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
         disabled:opacity-50 disabled:cursor-not-allowed;
}

.action-button-primary {
  @apply bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-500;
}

.action-button-success {
  @apply bg-green-600 text-white hover:bg-green-700 focus:ring-green-500;
}

.action-button-secondary {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500;
}
</style>
